// 멤버관리 탭 계정생성 모달
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  Text,
  Select,
  HStack,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { sendEmailCode, signup } from "../../../src/api";

export function AccountCreateModal({ isOpen, onClose, onSuccess }) {
  const toast = useToast();

  const [role, setRole] = useState("advertiser");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [authCode, setAuthCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loadingCode, setLoadingCode] = useState(false);
  const [loadingCreate, setLoadingCreate] = useState(false);

  // 이메일 정규식
  const isValidEmail = (value) =>
    /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(value);

  // 비밀번호 정규식 (8~16자, 영문 + 숫자 조합)
  const isValidPassword = (value) =>
    /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d!@#$%^&*()_+\-={}[\]|;:'",.<>/?]{8,16}$/.test(
      value
    );

  const handleSendCode = async () => {
    if (!isValidEmail(email)) {
      return toast({
        title: "잘못된 이메일",
        description: "올바른 이메일 주소를 입력해주세요.",
        status: "warning",
        duration: 2000,
      });
    }
    setLoadingCode(true);
    try {
      await sendEmailCode(email);
      toast({
        title: "인증번호 발송 완료",
        status: "success",
        duration: 2000,
      });
    } catch (e) {
      toast({
        title: "전송 실패",
        description:
          e?.response?.data?.message ??
          e?.message ??
          "코드 전송에 실패했습니다.",
        status: "error",
        duration: 2000,
      });
    } finally {
      setLoadingCode(false);
    }
  };

  const handleCreate = async () => {
    if (!name || !email || !password || !confirmPw || !authCode) {
      return toast({
        title: "필수 항목 미입력",
        status: "warning",
        duration: 2000,
      });
    }

    if (!isValidEmail(email)) {
      return toast({
        title: "잘못된 이메일",
        status: "warning",
        duration: 2000,
      });
    }

    if (!isValidPassword(password)) {
      return toast({
        title: "비밀번호 형식 오류",
        description: "8~16자리 / 영문 + 숫자 조합 필요",
        status: "warning",
        duration: 2000,
      });
    }

    if (password !== confirmPw) {
      return toast({
        title: "비밀번호 불일치",
        status: "error",
        duration: 2000,
      });
    }

    setLoadingCreate(true);

    const body = {
      email: email,
      password: password,
      displayName: name,
      role: role,
      consents: [
        {
          slug: "tos",
          revisionId: 12,
          consented: true,
        },
      ],
      locale: "ko-KR",
      channel: "web",
      verificationCode: authCode,
    };

    try {
      await signup(body);
      toast({
        title: "계정 생성 완료",
        status: "success",
        duration: 2000,
      });
      onClose();
      onSuccess?.(); // MemberPage 리스트 갱신 hook 실행
    } catch (err) {
      toast({
        title: "계정 생성 실패",
        description: err.response?.data?.message || "다시 시도해주세요",
        status: "error",
      });
    } finally {
      setLoadingCreate(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="20px" p="10px">
        <ModalHeader fontSize="22px">계정 생성</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="30px">
          <Text fontSize="14px" fontWeight="600" mb="6px">
            회원 유형{" "}
            <Text as="span" color="red.500">
              (필수)
            </Text>
          </Text>
          <Select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            mb="20px"
            h="46px"
          >
            <option value="creator">크리에이터</option>
            <option value="advertiser">광고주</option>
            <option value="agency">에이전시</option>
          </Select>

          <Text fontSize="14px" fontWeight="600" mb="6px">
            이름{" "}
            <Text as="span" color="red.500">
              (필수)
            </Text>
          </Text>
          <Input
            placeholder="ex) 홍길동"
            mb="20px"
            h="46px"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />

          <Text fontSize="14px" fontWeight="600" mb="6px">
            이메일 인증{" "}
            <Text as="span" color="red.500">
              (필수)
            </Text>
          </Text>
          <HStack mb="10px">
            <Input
              placeholder="ex) example@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              h="46px"
            />
            <Button
              h="46px"
              minW="90px"
              bg="#0054C0"
              color="white"
              fontSize="16px"
              fontWeight={700}
              onClick={handleSendCode}
              isLoading={loadingCode}
            >
              인증 번호
            </Button>
          </HStack>
          <Input
            placeholder="인증번호 입력"
            mb="20px"
            h="46px"
            value={authCode}
            onChange={(e) => setAuthCode(e.target.value)}
          />

          <Text fontSize="14px" fontWeight="600" mb="6px">
            비밀번호{" "}
            <Text as="span" color="red.500">
              (필수)
            </Text>
          </Text>
          <Input
            placeholder="8-16자리 / 영문, 숫자 조합"
            type="password"
            mb="20px"
            h="46px"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          <Text fontSize="14px" fontWeight="600" mb="6px">
            비밀번호 확인{" "}
            <Text as="span" color="red.500">
              (필수)
            </Text>
          </Text>
          <Input
            placeholder="한 번 더 입력"
            type="password"
            mb="30px"
            h="46px"
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
          />

          <Button
            w="104px"
            h="44px"
            bg="#0054C0"
            color="white"
            fontSize="14px"
            fontWeight={700}
            display="block"
            mx="auto"
            onClick={handleCreate}
            isLoading={loadingCreate}
          >
            생성
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
